import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

// 1. Keep Layouts and Root level components static (they are needed immediately)
import { RootLayout } from '../layouts/RootLayout'
import { AuthLayout } from '../layouts/AuthLayout'
import { PortalLayout } from '../layouts/PortalLayout'
import { ProtectedRoute } from '../components/auth/ProtectedRoute'
import { AuthGuard } from '../components/auth/AuthGuard'
import { PublicRoute } from '../components/auth/PublicRoute'
import { NotFound } from "../pages/NotFound"
import { AccessDenied } from "../pages/AccessDenied"
import { ErrorPage } from "../pages/ErrorPage"
import { PageLoader } from "../pages/PageLoader"
import { CreateRedirect, EditRedirect } from "./formRedirects"
import { withErrorElement } from "./withErrorElement"

// 2. Use lazy() for all Page components
// This creates separate small .js files for each page
const Login = lazy(() => import('../pages/auth/Login'))
const Register = lazy(() => import('../pages/auth/Register'))
const ManagePublicOfferings = lazy(() => import('../pages/portal/admin/PublicOfferings/ManagePublicOfferings'))
const Checkout = lazy(() => import('../pages/checkout/Checkout'))
const CheckoutReturn = lazy(() => import('../pages/checkout/CheckoutReturn'))
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'))
const VerifyCode = lazy(() => import('../pages/auth/VerifyCode'))
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'))
const SetupAccount = lazy(() => import('../pages/auth/SetupAccount'))
// Filenames now match their routes. Previously SetNewPassword.tsx served
// /first-time-setup and SetupAccountPassword.tsx served /set-new-password —
// i.e. they were swapped — and TermsAndConditions.tsx was a password form.
const FirstTimeSetup = lazy(() => import('../pages/auth/FirstTimeSetup'))
const SetNewPassword = lazy(() => import('../pages/auth/SetNewPassword'))
const ChangePassword = lazy(() => import('../pages/auth/ChangePassword'))
const Terms = lazy(() => import('../pages/auth/Terms'))
const Dashboard = lazy(() => import('../pages/portal/Dashboard'))
const Profile = lazy(() => import('../pages/portal/Profile'))
const Report = lazy(() => import('../pages/portal/Report'))
const Assessments = lazy(() => import('../pages/portal/Assessments'))
const AssessmentQuestion = lazy(() => import('../pages/portal/AssestmentQuestion'))
const AssessmentResult = lazy(() => import('../pages/portal/AssessmentResult'))
const Evaluations = lazy(() => import('../pages/portal/Evaluations'))
const EvaluationResult = lazy(() => import('../pages/portal/EvaluationResult'))
const MyLearningDetails = lazy(() => import('../pages/portal/MyLearningDetails'))
const JoinSession = lazy(() => import('../pages/portal/JoinSession'))
const Certificate = lazy(() => import('../pages/portal/Certificate'))
const Learning = lazy(() => import('../pages/portal/Learning'))
const LearningCourses = lazy(() => import('../pages/portal/LearningCourses'))
const LearningDetails = lazy(() => import('../pages/portal/LearningDetails'))
const Calendar = lazy(() => import('../pages/portal/Calendar'))
const Learners = lazy(() => import('../pages/portal/Learners'))
const Admins = lazy(() => import('../pages/portal/Admins'))
const TeamManagers = lazy(() => import('../pages/portal/TeamManagers'))
const SystemUsers = lazy(() => import('../pages/portal/SystemUsers'))
const Roles = lazy(() => import('../pages/portal/Roles'))
const ViewRole = lazy(() => import('../pages/portal/ViewRole'))
const Packages = lazy(() => import('../pages/portal/Packages'))
const SubscriptionRenewals = lazy(() => import('../pages/portal/SubscriptionRenewals'))
const ManageSubscriptions = lazy(() => import('../pages/portal/ManageSubscriptions'))
const ViewPackage = lazy(() => import('../pages/portal/ViewPackage'))
const ViewAssessment = lazy(() => import('../pages/portal/admin/Assessment/ViewAssessment'))
const ManageAssessments = lazy(() => import('../pages/portal/admin/Assessment/ManageAssessments'))
const ManageAssessmentTemplates = lazy(() => import('../pages/portal/admin/Assessment/ManageAssessmentTemplates'))
const ViewAssessmentTemplate = lazy(() => import('../pages/portal/admin/Assessment/ViewAssessmentTemplate'))
const AdminAssessmentSubmission = lazy(() => import('../pages/portal/admin/Assessment/AdminAssessmentSubmission'))
const ViewEvaluation = lazy(() => import('../pages/portal/admin/Evaluation/ViewEvaluation'))
const ManageEvaluationTemplates = lazy(() => import('../pages/portal/admin/Evaluation/ManageEvaluationTemplates'))
const ManageEvaluations = lazy(() => import('../pages/portal/admin/Evaluation/ManageEvaluations'))
const ViewEvaluationTemplate = lazy(() => import('../pages/portal/admin/Evaluation/ViewEvaluationTemplate'))
const AdminEvaluationSubmission = lazy(() => import('../pages/portal/admin/Evaluation/AdminEvaluationSubmission'))
const EvaluationQuestion = lazy(() => import('../pages/portal/EvaluationQuestion'))
const ViewUserDetail = lazy(() => import('../pages/portal/admin/Users/ViewUserDetail'))
const ManageImpersonations = lazy(() => import('../pages/portal/admin/Users/ManageImpersonations'))
const ManageSblKhas = lazy(() => import('../pages/portal/admin/SblKhas/ManageSblKhas'))
const ViewSblKhas = lazy(() => import('../pages/portal/admin/SblKhas/ViewSblKhas'))
const ManageWorkspaces = lazy(() => import('../pages/portal/admin/Workspaces/ManageWorkspaces'))
const ManagePartners = lazy(() => import('../pages/portal/admin/Partners/ManagePartners'))
const ReferralLeads = lazy(() => import('../pages/portal/admin/Partners/ReferralLeads'))
const ReferralAutoApprove = lazy(() => import('../pages/portal/admin/Partners/ReferralAutoApprove'))
const Invites = lazy(() => import('../pages/portal/admin/Partners/Invites'))
const ManageAutoApprove = lazy(() => import('../pages/portal/admin/AutoApprove/ManageAutoApprove'))
const ViewWorkspace = lazy(() => import('../pages/portal/admin/Workspaces/ViewWorkspace'))
const ManageOrganizationalUnitTypes = lazy(() => import('../pages/portal/admin/OrganizationalUnits/ManageOrganizationalUnitTypes'))
const ManageOrganizationalUnits = lazy(() => import('../pages/portal/admin/OrganizationalUnits/ManageOrganizationalUnits'))
const ManageCourses = lazy(() => import('../pages/portal/admin/Courses/ManageCourses'))
const ViewCourse = lazy(() => import('../pages/portal/admin/Courses/ViewCourse'))
const ManageKitItems = lazy(() => import('../pages/portal/admin/Courses/ManageKitItems'))
const ManageAddons = lazy(() => import('../pages/portal/admin/Courses/ManageAddons'))
const ManageCoursePillars = lazy(() => import('../pages/portal/admin/CoursePillars/ManageCoursePillars'))
const ManageCoursePillarTypes = lazy(() => import('../pages/portal/admin/CoursePillars/ManageCoursePillarTypes'))
const ManageLearnings = lazy(() => import('../pages/portal/admin/Learning/ManageLearnings'))
const ViewLearning = lazy(() => import('../pages/portal/admin/Learning/ViewLearning'))
const ManageLearningSlots = lazy(() => import('../pages/portal/admin/LearningSlots/ManageLearningSlots'))
const ManageBookings = lazy(() => import('../pages/portal/admin/Bookings/ManageBookings'))
const ManageAttendance = lazy(() => import('../pages/portal/admin/Attendance/ManageAttendance'))
const ManageAttendanceReviews = lazy(() => import('../pages/portal/admin/Attendance/ManageAttendanceReviews'))
const ViewLearningSlot = lazy(() => import('../pages/portal/admin/LearningSlots/ViewLearningSlot'))
const ManageCertificateTemplates = lazy(() => import('../pages/portal/admin/Certificate/ManageCertificateTemplates'))
const ViewCertificateTemplate = lazy(() => import('../pages/portal/admin/Certificate/ViewCertificateTemplate'))
const CertificateDesigner = lazy(() => import('../pages/portal/admin/Certificate/CertificateDesigner'))
const ManageCertificates = lazy(() => import('../pages/portal/admin/Certificate/ManageCertificates'))
const ExamVoucherAction = lazy(() => import('../pages/public/ExamVoucherAction'))
const AddonInvoiceAction = lazy(() => import('../pages/public/AddonInvoiceAction'))
const InvoiceAction = lazy(() => import('../pages/public/InvoiceAction'))
const ImpersonateBootstrap = lazy(() => import('../pages/portal/ImpersonateBootstrap'))
const TimeOverridePage = lazy(() => import('../pages/portal/dev/TimeOverridePage'))
const OpsConsole = lazy(() => import('../pages/portal/admin/Ops/OpsConsole'))
const ManageInvoices = lazy(() => import('../pages/portal/admin/Invoices/ManageInvoices'))
const CompanyBilling = lazy(() => import('../pages/portal/CompanyBilling'))
const ManagePayments = lazy(() => import('../pages/portal/admin/Payments/ManagePayments'))
const Help = lazy(() => import('../pages/portal/Help'))

// Error pages, innermost first: a portal page that crashes shows it in the
// portal's content area (sidebar and top bar keep working); PortalLayout itself
// crashing shows it full page with "Go to dashboard"; anything else (public and
// auth pages, the top-level pages below) full page with "Go to home". A
// stale-deploy chunk failure reloads once first. Unknown URLs never get here:
// the '*' route renders NotFound.
export const router = createBrowserRouter(withErrorElement(<ErrorPage />, [
  {
    element: (
      // 4. Wrap the Root in Suspense to handle the loading of chunks
      <Suspense fallback={<PageLoader />}>
        <RootLayout />
      </Suspense>
    ),
    children: [
      // Public routes (login/register/password flows)
      {
        element: <PublicRoute />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: '/', element: <Login /> },
              { path: '/register', element: <Register /> },
              // Self-serve package purchase. Public, because nobody buying a
              // package has an account yet — the account is what they are
              // buying. The return path is where the payment provider sends
              // them back, and reads the outcome from the server rather than
              // from the URL it arrived on.
              { path: '/checkout', element: <Checkout /> },
              { path: '/checkout/return', element: <CheckoutReturn /> },
              { path: '/checkout/cancelled', element: <CheckoutReturn /> },
              { path: '/forgot-password', element: <ForgotPassword /> },
              { path: '/verify-code', element: <VerifyCode /> },
              { path: '/reset-password', element: <ResetPassword /> },
              { path: '/setup-account', element: <SetupAccount /> },
              { path: '/set-new-password', element: <SetNewPassword /> },
              // Public terms document — Register's consent link needs to be
              // readable before you have an account.
              { path: '/terms', element: <Terms /> },
              { path: '/exam-voucher/action', element: <ExamVoucherAction /> },
              { path: '/addon-invoice/action', element: <AddonInvoiceAction /> },
              // Finance's approve / reject / record-payment link for ledger invoices
              { path: '/invoice/action', element: <InvoiceAction /> },
            ]
          },
        ]
      },

      // Semi-protected: requires login but bypasses portal (first-time login flows)
      {
        element: <AuthGuard />,
        children: [
          // Moved off /terms, which now serves the real terms document.
          // NOTE: nothing currently navigates here, and /first-time-setup does
          // the same job (both POST /auth/change-password and call
          // completeFirstTimeLogin). Kept rather than deleted pending a call on
          // whether it's still needed.
          { path: '/change-password', element: <ChangePassword /> },
          { path: '/first-time-setup', element: <FirstTimeSetup /> },
        ]
      },
      
      // Protected routes
      {
        element: <AuthGuard />,
        children: [
          {
            path: '/portal',
            element: <PortalLayout />,
            errorElement: <ErrorPage home="portal" />,
            children: withErrorElement(<ErrorPage variant="content" home="portal" />, [
              { index: true, element: <Dashboard /> },
              { path: 'profile', element: <Profile /> },
              // Help / user guides — open to every authenticated role.
              { path: 'help', element: <Help /> },
              { path: 'help/:slug', element: <Help /> },
              {
                // Company Billing: access follows the "Company Billing" menu
                // entry (billing.menu permission), whatever the role.
                element: <ProtectedRoute />,
                children: [{ path: 'company-billing', element: <CompanyBilling /> }],
              },
              {
                element: <ProtectedRoute allowedRoles={['admin', 'team_manager']} />,
                children: [
                  { path: 'learner', element: <Learners /> },
                  { path: 'evaluations', element: <Evaluations /> },
                  { path: 'report', element: <Report /> }
                ]
              },
              {
                element: <ProtectedRoute allowedRoles={['admin', 'learner']} />,
                children: [{ path: 'assessments', element: <Assessments /> }]
              },
                {
                element: <ProtectedRoute allowedRoles={['learner']} />,
                children: [
                  // Was /portal?tab=upcoming#recommend-training-card — the
                  // Overview tabs block, now commented out for design parity,
                  // so that landed on an Overview that ignored both the param
                  // and the anchor. The learner's own course list is the
                  // closest equivalent to the old My Learning listing.
                  { path: 'mylearning', element: <Navigate to="/portal/learning?enrolled=1" replace /> },
                  { path: 'certificate', element: <Certificate /> },
                  { path: 'learning', element: <Learning /> },
                  { path: 'learning/courses', element: <LearningCourses /> },
                  { path: 'calendar', element: <Calendar /> },
                  { path: 'evaluations', element: <Evaluations /> },
                  { path: 'mylearning/:id', element: <MyLearningDetails /> },
                  { path: 'learning/:id', element: <LearningDetails /> },
                  { path: 'assessment/:id', element: <AssessmentQuestion /> },
                  { path: 'evaluation/:id', element: <EvaluationQuestion /> },
                  { path: 'join-session', element: <JoinSession /> },
                  { path: 'join-session/:id', element: <JoinSession /> },
                  { path: 'assessment-result/:id', element: <AssessmentResult /> },
                  { path: 'evaluation-result/:id', element: <EvaluationResult /> },
                ]
              },
              {
                element: <ProtectedRoute allowedRoles={['admin', 'super_admin']} />,
                children: [
                  { path: 'users/learners', element: <Learners /> },
                  { path: 'users/team-managers', element: <TeamManagers /> },
                  { path: 'users/admins', element: <Admins /> },
                  { path: 'users/roles', element: <Roles /> },
                  { path: 'users/roles/:id', element: <ViewRole /> },
                ]
              },
              {
                element: <ProtectedRoute allowedRoles={['admin', 'team_manager', 'super_admin']} />,
                children: [
                  { path: 'users/:type/:id', element: <ViewUserDetail /> },
                ]
              },
              {
                element: <ProtectedRoute allowedRoles={['super_admin']} />,
                children: [
                  { path: 'users/system', element: <SystemUsers /> },
                  { path: 'users/impersonations', element: <ManageImpersonations /> },
                  { path: 'partners', element: <ManagePartners /> },
                  { path: 'partners/auto-approve', element: <ReferralAutoApprove /> },
                  { path: 'packages', element: <Packages /> },
                  { path: 'subscriptions/renewals', element: <SubscriptionRenewals /> },
                  { path: 'subscriptions', element: <ManageSubscriptions /> },
                  { path: 'packages/:id', element: <ViewPackage /> },
                  { path: 'admin/organizational-unit-types', element: <ManageOrganizationalUnitTypes /> },
                  { path: 'workspaces', element: <ManageWorkspaces /> },
                  { path: 'workspaces/create', element: <CreateRedirect to="/portal/workspaces" /> },
                  { path: 'workspaces/:id', element: <ViewWorkspace /> },
                  { path: 'workspaces/:id/edit', element: <EditRedirect to="/portal/workspaces" /> },
                  { path: 'admin/auto-approve', element: <ManageAutoApprove /> },
                  // Hidden dev tool: tester clock-shift panel. The page itself
                  // redirects when the feature is not compiled in (i.e. prod).
                  { path: 'dev/time', element: <TimeOverridePage /> },
                  // Ops console. Unlike dev/time this is intended to work in
                  // every environment — the backend gates it on super.admin,
                  // not on a build flag.
                  { path: 'admin/ops', element: <OpsConsole /> },
                  // Invoice ledger (super admin), gated server-side like admin/ops.
                  { path: 'admin/invoices', element: <ManageInvoices /> },
                  { path: 'admin/payments', element: <ManagePayments /> },
                ]
              },
              {
                // SBL-Khas: accessible by super_admin, business_admin, admin (HR Admin)
                element: <ProtectedRoute allowedRoles={['super_admin', 'business_admin', 'admin']} />,
                children: [
                  { path: 'sbl-khas', element: <ManageSblKhas /> },
                  { path: 'sbl-khas/:id', element: <ViewSblKhas /> },
                ]
              },
              {
                // Referral leads: partner + HR admins (and super admin) review/approve.
                element: <ProtectedRoute allowedRoles={['super_admin', 'business_admin', 'admin', 'partner']} />,
                children: [
                  { path: 'partner/leads', element: <ReferralLeads /> },
                  { path: 'partner/invites', element: <Invites /> },
                ]
              },
              {
                // Assessment admin list + view: readable by team_manager (buttons gated by usePermissions)
                element: <ProtectedRoute allowedRoles={['admin', 'super_admin', 'team_manager']} />,
                children: [
                  { path: 'admin/assessments', element: <ManageAssessments /> },
                  { path: 'admin/assessment/:id', element: <ViewAssessment /> },
                  { path: 'admin/assessment-submission/:responseId', element: <AdminAssessmentSubmission /> },
                ]
              },
              {
                // Evaluation list + view: readable by team_manager (buttons gated by usePermissions)
                element: <ProtectedRoute allowedRoles={['admin', 'super_admin', 'team_manager']} />,
                children: [
                  { path: 'admin/evaluations', element: <ManageEvaluations /> },
                  { path: 'admin/evaluation/:id', element: <ViewEvaluation /> },
                  { path: 'admin/evaluation-submission/:responseId', element: <AdminEvaluationSubmission /> },
                ]
              },
              {
                // Assessment/Evaluation full CRUD (create, edit, delete, templates)
                element: <ProtectedRoute allowedRoles={['admin', 'super_admin']} />,
                children: [
                  { path: 'admin/assessment/create', element: <CreateRedirect to="/portal/admin/assessments" /> },
                  { path: 'admin/assessment/:id/edit', element: <EditRedirect to="/portal/admin/assessments" /> },
                  { path: 'admin/assessment-template/create', element: <CreateRedirect to="/portal/admin/assessment-templates" /> },
                  { path: 'admin/assessment-templates', element: <ManageAssessmentTemplates /> },
                  { path: 'admin/assessment-template/:id', element: <ViewAssessmentTemplate /> },
                  { path: 'admin/assessment-template/:id/edit', element: <EditRedirect to="/portal/admin/assessment-templates" /> },
                  { path: 'admin/evaluation/create', element: <CreateRedirect to="/portal/admin/evaluations" /> },
                  { path: 'admin/evaluation/:id/edit', element: <EditRedirect to="/portal/admin/evaluations" /> },
                  { path: 'admin/evaluation-template/create', element: <CreateRedirect to="/portal/admin/evaluation-templates" /> },
                  { path: 'admin/evaluation-templates', element: <ManageEvaluationTemplates /> },
                  { path: 'admin/evaluation-template/:id', element: <ViewEvaluationTemplate /> },
                  { path: 'admin/evaluation-template/:id/edit', element: <EditRedirect to="/portal/admin/evaluation-templates" /> },
                ]
              },
              {
                // Super admin only: organizational units
                element: <ProtectedRoute allowedRoles={['super_admin']} />,
                children: [
                  { path: 'admin/organizational-units', element: <ManageOrganizationalUnits /> },
                ]
              },
              {
                element: <ProtectedRoute allowedRoles={['admin', 'super_admin']} />,
                children: [
                  { path: 'admin/courses', element: <ManageCourses /> },
                  { path: 'admin/course/create', element: <CreateRedirect to="/portal/admin/courses" /> },
                  { path: 'admin/course/:id', element: <ViewCourse /> },
                  { path: 'admin/course/:id/edit', element: <EditRedirect to="/portal/admin/courses" /> },
                  { path: 'admin/kit-items', element: <ManageKitItems /> },
                  { path: 'admin/addons', element: <ManageAddons /> },
                  { path: 'admin/course-pillars', element: <ManageCoursePillars /> },
                  { path: 'admin/course-pillar-types', element: <ManageCoursePillarTypes /> },
                  // What is on sale to the public. Super admin only on the
                  // server too — pricing a package and putting it in front of
                  // strangers is a commercial decision.
                  { path: 'admin/public-packages', element: <ManagePublicOfferings /> },
                  { path: 'admin/course-pillar/create', element: <CreateRedirect to="/portal/admin/course-pillars" /> },
                  { path: 'admin/course-pillar/:id/edit', element: <EditRedirect to="/portal/admin/course-pillars" /> },
                  { path: 'admin/learnings', element: <ManageLearnings /> },
                  { path: 'admin/learning/create', element: <CreateRedirect to="/portal/admin/learnings" /> },
                  { path: 'admin/learning/:id', element: <ViewLearning /> },
                  { path: 'admin/learning/:id/edit', element: <EditRedirect to="/portal/admin/learnings" /> },
                  { path: 'admin/learning-slots', element: <ManageLearningSlots /> },
                  { path: 'admin/bookings', element: <ManageBookings /> },
                  { path: 'admin/attendance', element: <ManageAttendance /> },
                  { path: 'admin/attendance-reviews', element: <ManageAttendanceReviews /> },
                  { path: 'admin/learning-slot/create', element: <CreateRedirect to="/portal/admin/learning-slots" /> },
                  { path: 'admin/learning-slot/:id', element: <ViewLearningSlot /> },
                  { path: 'admin/learning-slot/:id/edit', element: <EditRedirect to="/portal/admin/learning-slots" /> },
                  { path: 'admin/certificate-templates', element: <ManageCertificateTemplates /> },
                  { path: 'admin/certificate-template/create', element: <CreateRedirect to="/portal/admin/certificate-templates" /> },
                  { path: 'admin/certificate-template/:id', element: <ViewCertificateTemplate /> },
                  { path: 'admin/certificate-template/:id/edit', element: <EditRedirect to="/portal/admin/certificate-templates" /> },
                  { path: 'admin/certificate-template/:id/design', element: <CertificateDesigner /> },
                  { path: 'admin/certificates', element: <ManageCertificates /> },
                ]
              },
              { path: 'assessment/:id', element: <AssessmentQuestion /> },
                  { path: 'join-session/:id', element: <JoinSession /> },
                  { path: 'assessment-result/:id', element: <AssessmentResult /> },
                ])
              },
            ]
          },
        ]
      },
      
      // 404 Not Found and Access Denied
      { path: 'access-denied', element: <AccessDenied /> },
      { path: 'page-loader', element: <PageLoader /> },
      // Impersonation handshake landing page — unauthed; exchanges the one-time
      // token in the URL hash for a sessionStorage-backed login.
      { path: 'impersonate-bootstrap', element: <ImpersonateBootstrap /> },
      { path: '*', element: <NotFound /> }
]))

// Dev-only affordance for the screenshot harness (screenshots/capture.mjs): a
// reliable client-side navigation entry point that keeps the authenticated
// session alive (full-page reloads drop it). Guarded by import.meta.env.DEV, so
// it is stripped from every production build.
if (import.meta.env.DEV) {
  (window as unknown as { __router?: typeof router }).__router = router;
}